import React, {useState} from 'react';
import {ActivityIndicator, Pressable, StyleProp, View, ViewStyle} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';
import {F, S, STATUS, T} from '../courseTheme';
import {EngineLine, EngineState, EngineStatus, evalSide, evalWord, formatSanLine} from './engineLogic';

type Props = {
  enabled: boolean;
  onToggle: (next: boolean) => void;
  state: EngineState;
  status: EngineStatus;
  /** Plies shown per line before it is cut with "…". */
  maxPlies?: number;
  /** Makes each line a button. */
  onPlayLine?: (san: string[]) => void;
  /** Hover (web) on a line — e.g. to preview its arrow. Null on leave. */
  onHoverLine?: (line: EngineLine | null) => void;
  /** Rendered under the eval readout while the engine is running. */
  children?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
};

const ENDED: Partial<Record<EngineStatus, string>> = {
  checkmate: 'Checkmate',
  stalemate: 'Stalemate',
  draw: 'Draw',
};

/** A focus ring for web keyboard users; RN's style types do not know outline. */
const ring = (on: boolean) =>
  ({outlineWidth: on ? 2 : 0, outlineColor: F.tealDark, outlineStyle: 'solid', outlineOffset: 2} as any);

function EngineSwitch({enabled, onToggle}: {enabled: boolean; onToggle: (next: boolean) => void}) {
  const [focused, setFocused] = useState(false);
  return (
    <Pressable
      accessibilityRole="switch"
      accessibilityState={{checked: enabled}}
      accessibilityLabel="Engine analysis"
      onPress={() => onToggle(!enabled)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={({pressed}) => [
        {
          flexDirection: 'row',
          alignItems: 'center',
          gap: S[2],
          minHeight: 44,
          minWidth: 44,
          paddingHorizontal: S[2],
          borderRadius: 999,
          backgroundColor: pressed ? 'rgba(38,35,34,0.06)' : 'transparent',
          cursor: 'pointer',
        } as any,
        ring(focused),
      ]}>
      <CCText style={{fontSize: T.sm, fontWeight: '700', color: enabled ? F.tealDark : F.mutedText}}>
        {enabled ? 'On' : 'Off'}
      </CCText>
      {/* Track colours are the 4.5:1+ tokens, so the control reads as one. */}
      <View
        style={{
          width: 40,
          height: 24,
          borderRadius: 12,
          padding: 2,
          justifyContent: 'center',
          alignItems: enabled ? 'flex-end' : 'flex-start',
          backgroundColor: enabled ? F.tealDark : F.mutedText,
        }}>
        <View style={{width: 20, height: 20, borderRadius: 10, backgroundColor: F.white}} />
      </View>
    </Pressable>
  );
}

/** Which side the eval favours, as a shape — never colour alone; the word sits beside it. */
function SideSwatch({side}: {side: 'white' | 'black' | 'equal' | null}) {
  const box = {width: 12, height: 12, borderRadius: 3, borderWidth: 1, borderColor: F.brownDark, overflow: 'hidden' as const};
  if (side === 'equal') {
    return (
      <View style={[box, {flexDirection: 'row'}]}>
        <View style={{flex: 1, backgroundColor: F.white}} />
        <View style={{flex: 1, backgroundColor: F.brownDark}} />
      </View>
    );
  }
  return <View style={[box, {backgroundColor: side === 'black' ? F.brownDark : F.white}]} />;
}

function Notice({icon, title, detail}: {icon: string; title: string; detail: string}) {
  return (
    <View
      accessibilityLiveRegion="polite"
      style={{flexDirection: 'row', gap: S[2], padding: S[3], borderRadius: 10, backgroundColor: STATUS.unknown.bg}}>
      <Ionicons name={icon} size={18} color={F.brown} />
      <View style={{flex: 1, gap: 2}}>
        <CCText style={{fontSize: T.sm, fontWeight: '600', color: F.brownDark, lineHeight: 19}}>{title}</CCText>
        <CCText style={{fontSize: T.xs, color: F.brown, lineHeight: 17}}>{detail}</CCText>
      </View>
    </View>
  );
}

function LineRow({
  line,
  fen,
  maxPlies,
  onPlayLine,
  onHoverLine,
}: {
  line: EngineLine;
  fen: string;
  maxPlies: number;
  onPlayLine?: (san: string[]) => void;
  onHoverLine?: (line: EngineLine | null) => void;
}) {
  const [focused, setFocused] = useState(false);
  const moves = formatSanLine(fen, line.san, maxPlies);
  const content = (
    <>
      <View
        style={{
          minWidth: 56,
          paddingHorizontal: S[2],
          paddingVertical: 3,
          borderRadius: 6,
          backgroundColor: F.badgeBg,
          alignItems: 'center',
        }}>
        <CCText style={{fontSize: T.xs, fontWeight: '700', color: F.brownDark, fontVariant: ['tabular-nums']}}>
          {line.display}
        </CCText>
      </View>
      <CCText style={{flex: 1, fontSize: T.sm, color: F.brownDark, lineHeight: 19}}>{moves}</CCText>
    </>
  );
  const row = {flexDirection: 'row' as const, alignItems: 'center' as const, gap: S[3], paddingVertical: S[1]};

  if (!onPlayLine) {
    return (
      <View accessible accessibilityLabel={`Line ${line.rank}: ${line.display}, ${moves}`} style={row}>
        {content}
      </View>
    );
  }
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`Play line ${line.rank}: ${line.display}, ${moves}`}
      onPress={() => onPlayLine(line.san)}
      onHoverIn={() => onHoverLine?.(line)}
      onHoverOut={() => onHoverLine?.(null)}
      onFocus={() => {
        setFocused(true);
        onHoverLine?.(line);
      }}
      onBlur={() => {
        setFocused(false);
        onHoverLine?.(null);
      }}
      style={({pressed, hovered}: any) => [
        row,
        {
          minHeight: 44,
          paddingHorizontal: S[2],
          marginHorizontal: -S[2],
          borderRadius: 8,
          backgroundColor: pressed ? 'rgba(38,35,34,0.08)' : hovered ? 'rgba(38,35,34,0.04)' : 'transparent',
          cursor: 'pointer',
        } as any,
        ring(focused),
      ]}>
      {content}
    </Pressable>
  );
}

/**
 * Stockfish's view of the current position: eval in figures and words, depth,
 * and the top lines. Presentational — feed it `useCourseEngine`'s output.
 *
 * Draws no card chrome and no board, and sets no fixed heights, so it can sit
 * inside a scrolling column.
 */
export default function CourseEnginePanel({
  enabled,
  onToggle,
  state,
  status,
  maxPlies = 8,
  onPlayLine,
  onHoverLine,
  children,
  style,
}: Props) {
  const best = state.lines[0];
  const thinking = status === 'starting' || status === 'thinking';
  const ended = ENDED[status];

  let body: React.ReactNode;
  if (status === 'off') {
    body = (
      <CCText style={{fontSize: T.sm, color: F.brown, lineHeight: 19}}>
        Turn it on to see Stockfish’s evaluation and best lines for this position.
      </CCText>
    );
  } else if (status === 'unavailable') {
    body = (
      <Notice
        icon="information-circle-outline"
        title="Engine couldn't start in this browser"
        detail="Everything else on this page works without it."
      />
    );
  } else if (ended) {
    body = <Notice icon="flag-outline" title={ended} detail="Nothing left to analyse in this position." />;
  } else {
    body = (
      <>
        {best ? (
          <View style={{gap: S[1]}}>
            <View style={{flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', columnGap: S[3], rowGap: S[1]}}>
              <CCText style={{fontSize: 28, fontWeight: '700', color: F.brownDark, lineHeight: 34, fontVariant: ['tabular-nums']}}>
                {best.display}
              </CCText>
              <View style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
                <SideSwatch side={evalSide(best.score)} />
                <CCText style={{fontSize: T.sm, fontWeight: '600', color: F.brownDark}}>{evalWord(best.score)}</CCText>
              </View>
            </View>
            <View style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
              {thinking ? <ActivityIndicator size="small" color={F.tealDark} /> : null}
              <CCText style={{fontSize: T.xs, color: F.mutedText, fontVariant: ['tabular-nums']}}>
                {thinking ? `Analysing · depth ${state.depth}` : `Depth ${state.depth}`}
              </CCText>
            </View>
          </View>
        ) : (
          <View style={{flexDirection: 'row', alignItems: 'center', gap: S[2], minHeight: 34}}>
            <ActivityIndicator size="small" color={F.tealDark} />
            <CCText style={{fontSize: T.sm, color: F.brown}}>
              {status === 'starting' ? 'Starting the engine…' : 'Analysing…'}
            </CCText>
          </View>
        )}
        {children}
        {state.lines.length ? (
          <View style={{borderTopWidth: 1, borderTopColor: F.border10, paddingTop: S[2], gap: 2}}>
            {state.lines.map(l => (
              <LineRow
                key={l.rank}
                line={l}
                fen={state.fen}
                maxPlies={maxPlies}
                onPlayLine={onPlayLine}
                onHoverLine={onHoverLine}
              />
            ))}
          </View>
        ) : null}
      </>
    );
  }

  return (
    <View style={[{gap: S[3]}, style]}>
      <View style={{flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: S[2]}}>
        <View style={{flexDirection: 'row', alignItems: 'center', gap: S[2], flexShrink: 1}}>
          <Ionicons name="analytics-outline" size={18} color={F.brownDark} />
          <CCText accessibilityRole="header" style={{fontSize: T.base, fontWeight: '700', color: F.brownDark}}>
            Engine
          </CCText>
        </View>
        <EngineSwitch enabled={enabled} onToggle={onToggle} />
      </View>
      {body}
    </View>
  );
}
